// useDeleteTask.js

import { useMutation } from "@apollo/client";
import { DELETE_TASK } from "../../graphql/mutations/ProjectEditContext";

/**
 * Custom hook to delete a task using GraphQL mutation.
 *
 * @returns {[Function, Object]} A function to delete the task and an object containing data, error, and loading states.
 */
const useDeleteTask = () => {
  const [mutation, { data, error, loading }] = useMutation(DELETE_TASK);

  /**
   * Function to delete a task with the provided parameters.
   *
   * @param {DeleteTaskParams} params - The task deletion parameters.
   * @returns {DeleteTaskResult|undefined} The result of the mutation.
   *
   * 1. If the mutation fails due to network or server issues, `undefined` is returned.
   * 2. If the mutation is executed but rejected by the server (e.g., task deletion failed),
   *    a `DeleteTaskResult` object with `success: false` and an `errorResult` is returned.
   */
  const deleteTask = async ({ projectId, taskId }) => {
    const variables = {
      deleteTaskParams: {
        projectId,
        taskId,
      }
    };

    const res = await mutation({ variables });

    // Return the result object, which contains success and errorResult, or undefined if the deletion failed.
    return res?.data?.projects?.deleteTask;
  };

  return [deleteTask, { data, error, loading }];
};

/**
 * @typedef {Object} DeleteTaskParams
 * @property {number} projectId - The project ID.
 * @property {number} taskId - The task ID to delete.
 */

/**
 * @typedef {Object} DeleteTaskResult
 * @property {boolean} success - Indicates whether the task deletion was successful.
 * @property {Object|null} errorResult - Present only if the deletion failed. Contains details of the error.
 */

/**
* Exporting the custom hook for external usage.
*/
export { useDeleteTask };
